'use client';

import * as React from 'react';
import { kycStatus, Player, PlayerKycV2, PlayersKycList } from '@alien-platform/bo-core';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CheckCircle } from '@phosphor-icons/react/dist/ssr/CheckCircle';
import { SpinnerGap } from '@phosphor-icons/react/dist/ssr/SpinnerGap';
import { XCircle } from '@phosphor-icons/react/dist/ssr/XCircle';
import dayjs from 'dayjs';
import { toast } from 'sonner';

import { InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';
import ItemsNotFound from '@/components/core/items-not-found';

import { getDocumentsList } from '../helpers';
import { KycItemDetails } from './kyc-item-details';

interface KycNoteDto {
  noteForPlayer: string;
}

export function PlayerKYCTabV2({ playerData }: { playerData: Player | undefined }): React.JSX.Element {
  const [kycData, setKycData] = React.useState<PlayerKycV2[]>([]);
  const [dto, setDto] = React.useState<KycNoteDto[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingData, setIsLoadingData] = React.useState(false);

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
    item: PlayerKycV2,
    itemIndex: number
  ): Promise<void> => {
    e.preventDefault();

    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
    const buttonAction = submitter.getAttribute('data-action');

    setIsLoading(true);
    try {
      const res = await fetch(`/api/players/kyc`, {
        method: 'PUT',
        body: JSON.stringify({
          kycId: item?.kycId,
          noteForPlayer: dto[itemIndex].noteForPlayer,
          action: buttonAction,
        }),
      });

      const data = await res.json();

      if (data.success && data.data) {
        toast.success(`Document has been successfully ${buttonAction === 'approve' ? 'approved' : 'rejected'}`);
      } else {
        toast.error(`${data?.messages}`);
      }
    } catch (error) {
      toast.error(`An error occurred`);
    } finally {
      setIsLoading(false);
    }
  };

  const getData = async (): Promise<PlayersKycList | null | undefined> => {
    setIsLoadingData(true);
    try {
      const res = await getDocumentsList({
        page: 1,
        limit: 50,
        playerCode: playerData?.playerCode,
      });

      if (res?.kyc) {
        const filteredKyc = res.kyc.filter((item) => item.kycStatus !== kycStatus.NOT_SUBMITTED);
        setKycData(filteredKyc);
      }
      return res;
    } catch (error) {
      return null;
    } finally {
      setIsLoadingData(false);
    }
  };

  React.useEffect(() => {
    if (playerData) {
      void getData();
    }
  }, [playerData?.playerCode]);

  return (
    <Stack direction="column">
      {isLoadingData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" size={38} />
        </Box>
      ) : (
        <Stack gap={2}>
          {!kycData.length && !isLoadingData ? (
            <ItemsNotFound />
          ) : (
            kycData.map((item, index) => (
              <Accordion defaultExpanded key={index}>
                <AccordionSummary expandIcon={<CaretDown />} aria-controls="panel1-content" id="panel1-header">
                  <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                    {item.kycStatus === kycStatus.PENDING ? (
                      <Tooltip title="Pending">
                        <SpinnerGap size={24} color="var(--mui-palette-warning-500)" />
                      </Tooltip>
                    ) : item.kycStatus === kycStatus.CONFIRMED ? (
                      <Tooltip title="Approved">
                        <CheckCircle size={24} color="var(--mui-palette-success-main)" />
                      </Tooltip>
                    ) : item.kycStatus === kycStatus.REJECTED ? (
                      <Tooltip title="Rejected">
                        <XCircle size={24} color="var(--mui-palette-error-main)" />
                      </Tooltip>
                    ) : null}

                    <Typography variant="h6">KYC LEVEL: {item.level}</Typography>
                    {item.kycStatus !== kycStatus.NOT_SUBMITTED ? (
                      <Chip variant="soft" label={dayjs(item?.createdAt).format('YYYY/MM/DD - HH:mm:ss')} />
                    ) : null}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  <form
                    onSubmit={(e) => {
                      void handleSubmit(e, item, index);
                    }}
                  >
                    <Stack gap={2}>
                      <KycItemDetails kycItem={item} />
                      <CustomConfigInputV2
                        type={InputType.MULTILINE_INPUT}
                        label="Note for player"
                        value={dto[index]?.noteForPlayer ?? ''}
                        defaultData={dto}
                        setDefaultData={setDto}
                        name={`${index}.noteForPlayer`}
                        required
                      />
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="error"
                          data-action="reject"
                          loading={isLoading}
                        >
                          Reject
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          data-action="approve"
                          loading={isLoading}
                        >
                          Approve
                        </Button>
                      </Box>
                    </Stack>
                  </form>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Stack>
      )}
    </Stack>
  );
}
